/**
 * 共通関数Helper
 */
import dayjs from 'dayjs';
import { useI18n } from './useUtils';

/**
 * JSONのシリアライズ、デシリアライズを拡張するオブジェクト
 * Javascriptのデフォルト動作だと、Date型をシリアライズするときに、
 * タイムゾーン情報が消し飛ぶので、文字列だけではUTC日付だということしかわからない。
 * このため、デシリアライズ時におかしなことになるので、拡張せざるを得ない
 *
 * JSON.stringify(XXX,CustomJSON.replacer)
 * JSON.parse(XXX,CustomJSON.reviver)
 *
 * 今は下記の様なパターンの文字列をデシリアライズ可能
 *
 * 2012/12/12
 * 2012-12-12
 * 2012-12-12T23:59:59
 * 2012-12-12T23:59:59+09:00
 * 2012-12-12T23:59:59.999
 * 2012-12-12T23:59:59.999+09:00
 * 2012-12-12T23:59:59.999Z
 */
export const CustomJSON = {
  pattern_datetime:
    /(\d{4})(-|\/)(\d{2})(-|\/)(\d{2})(T(\d{2})\:(\d{2})\:(\d{2})(\.\d{3})?(([+-](\d{2})\:(\d{2}))|Z)?)?/,

  replacer(key: any, value: any) {
    // @ts-ignore
    if (this[key] instanceof Date) {
      return dayjs(value).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    }
    return value;
  },
  reviver: (key: any, value: any) => {
    if (CustomJSON.pattern_datetime.test(value)) {
      return dayjs(value).toDate();
    }
    return value;
  },
};

/**
 * 年齢計算
 *
 * @param {Date} [dateOfBirth]
 * @param {string} [defaultLabel='-']
 */
export function calcAge(dateOfBirth?: Date | null, defaultLabel: string = '-') {
  return dateOfBirth ? dayjs().diff(dateOfBirth, 'year') : defaultLabel;
}

/**
 * 過去日付判定
 *
 * @export
 * @param {(number | null)} [year]
 * @param {(number | null)} [month]
 * @param {(number | null)} [day]
 * @returns {boolean}
 */
export function isPastDate(
  year?: number | null,
  month?: number | null,
  day?: number | null
): boolean {
  if (!year || !month) {
    return false;
  }
  const today = dayjs();
  const compareDate = dayjs(`${year}-${month}-${day || 1}`);
  return today.isAfter(compareDate, day === null ? 'm' : 'd');
}

/**
 * 今日までの経過日数を取得
 * @param date
 */
export function diffDays(date?: Date | null) {
  if (date) {
    const today = dayjs();
    return today.diff(date, 'd');
  }
  return 0;
}

/**
 * 日付の共通フォーマット化
 * @param strDate
 * @param defaultLabel
 */
export function formatDate(
  target: Date | null | undefined,
  defaultLabel: string = '-'
): String {
  if (target) {
    return dayjs(target).format('YYYY.MM.DD');
  } else {
    return defaultLabel;
  }
}

/**
 * 引数をDate型に変換
 * @param anyDate 変換元
 */
export function toDate(anyDate: any): Date | null {
  if (anyDate instanceof Date) {
    return anyDate;
  } else if (typeof anyDate === 'string') {
    return dayjs(String(anyDate)).toDate();
  } else {
    return null;
  }
}

// オファーの残り日数計算
export function getOfferLeftHours(offerDate: string | Date) {
  const LIMIT = 20 * 24;
  const today = new Date();
  const targetday = new Date(offerDate);
  const diffTime = today.getTime() - targetday.getTime();
  return LIMIT - Math.floor(diffTime / (60 * 60 * 1000));
}

/**
 * 4月を開始月とした年度を取得
 *
 * @returns {number} - 年度(YYYY)
 */
export function getFiscalYear() {
  const year = new Date().getFullYear();
  const month = new Date().getMonth();
  return year + (month < 3 ? -1 : 0);
}

/**
 * 文字列内のURLをaタグに変換する
 * @param {string} content - 変換文字列
 * @returns {string} - 変換後文字列
 */
export function urlToAnchorTag(content?: string | null) {
  if (!content) return '';
  // aタグに変換
  return content.replace(
    /(?!href=')((ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?)/gi,
    '<a class="_innerlink" href="$1" target="_blank">$1</a>'
  );
}

/**
 * @param {string} url - 対象のURL
 * @returns {RegExp} - i18nに対応したURLチェック用のRegExpオブジェクト
 */
export function buildUrlRegExpWithI18n(url: string) {
  const { localeCodes } = useI18n();
  return new RegExp(`^(/(${localeCodes.join('|')}))?${url}(\\?.*)?$`);
}
